import { axisBottom, axisLeft, extent, line, max, min, scaleLinear, scaleTime, select, timeFormat } from 'd3'
import { nest } from 'd3-collection'
import moment from 'moment'

import { GROUP_TYPE } from '../../constants/Filter'
import logo from '../../img/PEXA.png'
import { GroupedIndex, Index } from '../../store/StateObjects'
import { ContextType } from '../RefinanceIndex'
import {
  capitalize,
  drawGraphCircle,
  drawVertLine,
  getFilterData,
  getIndexDetail,
  getLabel,
  getLineGraphColor,
  getMonthCount,
  getXAxisTickValues,
  getXAxisTimeFormat,
  getXScaleDomain,
} from '../util'

interface MarginType {
  top: number
  bottom: number
  left: number
  right: number
}

function drawPreview(svgCurrent: SVGSVGElement, margin: MarginType, context: ContextType, chartHight: number): void {
  const { indexData, indexDetailType, groupBy, indexType, state, selectedRegion, selected } = context
  const chartMargin: MarginType = {
    top: 0,
    left: 24,
    right: 32,
    bottom: 0,
  }
  const xWidth = 706
  const xHeight = 142
  const sortedData: Index[] = indexData.sort((a, b) => +a.week - +b.week)
  const xDomain = extent(sortedData, (d: Index) => d.week) as [Date, Date]
  const [startDate, endDate] = xDomain
  const startDateDisplay = moment(startDate).format('MMM YYYY')
  const endDateDisplay = moment(endDate).format('MMM YYYY')
  const dateRangeDisplay =
    startDateDisplay === endDateDisplay
      ? `${capitalize(startDateDisplay)}`
      : `${capitalize(startDateDisplay)} - ${capitalize(endDateDisplay)}`

  const filters = [
    {
      type: 'State',
      value: state.length > 1 ? 'Multiple selected' : getLabel(state[0], getFilterData(GROUP_TYPE.STATE)),
    },
    {
      type: 'Region',
      value:
        selectedRegion.length > 1 ? 'Multiple selected' : getLabel(selectedRegion[0], getFilterData(GROUP_TYPE.REGION)),
    },

    {
      type: 'Date',
      value: dateRangeDisplay,
    },
    {
      type: 'Index Type',
      value: indexType.length > 1 ? 'Multiple selected' : getLabel(indexType[0], getFilterData(GROUP_TYPE.INDEX_TYPE)),
    },
  ]
  const svg = select(svgCurrent)

  svg.style('background', 'white')
  const g = svg.select('.preview-container').attr('transform', `translate(${margin.left},${margin.top})`)

  const headerGroup = svg.select('.preview-header')

  headerGroup
    .append('text')
    .attr('x', 0)
    .attr('y', 20)
    .attr('class', 'svg-header')
    .text(`PEXA Refinance ${capitalize(indexDetailType)} Index`)
    .style('fill', '#3c4a58')
    .style('font-family', 'Quicksand')
    .style('font-size', '20px')
    .style('font-weight', '700')
    .style('line-height', '60px')

  headerGroup.append('image').attr('y', 0).attr('x', 583).attr('width', 117).attr('height', 21).attr('xlink:href', logo)

  const filterGroup = svg.select('.preview-filters').attr('transform', 'translate(0, 69)')

  let totalLength = 0
  const itemLengthList: number[] = []
  filters.forEach(({ type, value }) => {
    const text = filterGroup
      .append('text')
      .attr('class', 'filter-item')
      .style('font-family', 'Quicksand')
      .style('font-size', '10px')
      .style('line-height', '60px')
      .style('fill', 'white')

    text.append('tspan').attr('class', 'filter-type').text(`${type}: `).style('font-weight', '700')
    text.append('tspan').text(value)

    const length = text.node().getComputedTextLength()
    itemLengthList.push(length)
    totalLength += length
  })

  const marginLength = (752 - 48 - totalLength) / 3
  let nextYPosition = 0
  svg
    .selectAll('.filter-item')
    .style('fill', '#3c4a58')
    .attr('x', (d: Index, i: number) => {
      if (i === 0) {
        return 0
      }
      nextYPosition += itemLengthList[i - 1] + marginLength
      return nextYPosition
    })
  // draw lines
  const monthCount = getMonthCount(startDate, endDate)
  const isSingleMonth = monthCount === 1
  const xScaleDomain = getXScaleDomain(startDate, endDate)

  const xScale = scaleTime()
    .range([24 + 24, xWidth])
    .domain([...xScaleDomain])

  const yScale = scaleLinear()
    .range([xHeight, 0])
    .domain([
      min(sortedData, (d: Index) => getIndexDetail(indexDetailType, d).refi_index) - 30,
      max(sortedData, (d: Index) => getIndexDetail(indexDetailType, d).refi_index) + 30,
    ])

  const xTickValues = getXAxisTickValues(startDate, endDate)
  const xAxis = axisBottom(xScale)
    .tickValues(xTickValues)
    .tickFormat(timeFormat(getXAxisTimeFormat(isSingleMonth, false)))

  svg
    .select('.preview-x-axis')
    .attr('transform', `translate(0, ${xHeight + 96})`)
    .call(xAxis)

  svg.selectAll('.preview-x-axis line').style('visibility', 'hidden')
  svg
    .selectAll('.preview-x-axis text')
    .style('font-family', 'Libre Franklin')
    .style('font-size', xTickValues.length > 12 ? '8px' : '10px')

  const yAxis = axisLeft(yScale).tickSize(-(xWidth - chartMargin.right))

  const yAxisGroup = svg.select('.preview-y-axis').attr('transform', `translate(24, 96)`).call(yAxis)

  svg
    .selectAll('.preview-y-axis line')
    .attr('transform', 'translate(8, 0)')
    .style('stroke-width', '0.3px')
    .style('stroke', 'gray')

  svg.selectAll('.preview-y-axis path').style('visibility', 'hidden')
  svg.selectAll('.preview-x-axis path').style('visibility', 'hidden')

  svg
    .selectAll('.preview-y-axis line')
    .filter((value: number) => value === 100)
    .style('stroke-width', '2px')
    .style('stroke', '#1F2142')

  svg
    .append('text')
    .attr('y', 8)
    .attr('x', 0 - chartHight / 2)
    .attr('dy', '1em')
    .attr('class', 'preview-y-label')
    .style('text-anchor', 'middle')
    .text('Index')

  const groupedByKey: GroupedIndex[] = nest<Index>()
    .key((d) => d[groupBy])
    .entries(sortedData)

  const lineNo = groupedByKey.length

  const lineGen = () =>
    line()
      .x((d: Index) => xScale(d.week))
      .y((d: Index) => yScale(getIndexDetail(indexDetailType, d).refi_index))

  const u = g.selectAll('.line').data(groupedByKey)

  u.enter()
    .append('path')
    .attr('class', 'line')
    .merge(u)
    .transition()
    .duration(0)
    .attr('d', (d: GroupedIndex) => lineGen()(d.values))
    .attr('fill', 'none')
    .attr('stroke', (d: GroupedIndex) => getLineGraphColor(lineNo > 1 ? d.key : ''))
    .style('opacity', 1)
    .attr('transform', `translate(0, 96)`)
    .attr('fill', 'none')
    .attr('stroke', (d: GroupedIndex) => {
      return getLineGraphColor(lineNo > 1 ? d.key : '')
    })
    .attr('stroke-width', '2px')
    .on('end', (d: GroupedIndex) => {
      const { values } = d
      const lastData = values[values.length - 1]
      const { week } = lastData
      const index = getIndexDetail(indexDetailType, lastData).refi_index
      if (lineNo === 1) {
        drawVertLine(yAxisGroup, xScale(week) - 24, 'preview-vertical-line', 'preview-y-axis')
        drawGraphCircle(yAxisGroup, xScale(week) - 24, yScale(index), 'last-point', lineNo, '', '', 0, 4)
      }
    })

  const legendG = svg.select('.preview-legend').attr('transform', `translate(0, 288)`)

  let yPosition = 8
  selected.length > 1 &&
    selected.forEach((legend) => {
      legendG
        .append('circle')
        .attr('cx', yPosition)
        .attr('cy', 8)
        .attr('r', 6)
        .attr('stroke', getLineGraphColor(lineNo > 1 ? legend : ''))
        .attr('fill', getLineGraphColor(lineNo > 1 ? legend : ''))

      yPosition += 10

      const legendLabel = legendG
        .append('text')
        .attr('x', yPosition)
        .attr('y', 12)
        .text(getLabel(legend, getFilterData(groupBy)))
        .style('fill', '#3c4a58')
        .style('font-family', 'Libre Franklin')
        .style('font-size', '10px')
        .style('font-weight', '400')
        .style('line-height', '23px')

      yPosition += 14 + legendLabel.node().getComputedTextLength()
    })
  const time = moment(new Date()).format('hh:mm DD/MM/YYYY')
  const downloadDate = svg.select('.preview-date').attr('transform', `translate(0, 288)`)
  downloadDate
    .append('text')
    .attr('x', 752 - 24 - 16)
    .attr('y', 12)
    .attr('text-anchor', 'end')
    .text(`Downloaded: ${time}`)
    .style('fill', '#3c4a58')
    .style('font-family', 'Libre Franklin')
    .style('font-size', '10px')
    .style('font-weight', '300')
    .style('line-height', '28px')
}

export default drawPreview
